import { useState, useEffect, Fragment } from "react";
import { TextField, styled } from "@mui/material";
import clsx from "clsx";

const CustomizedTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "disabledBgColor",
})(({ disabledBgColor }) => ({
  "&.MuiFormControl-root": {
    marginTop: 5,
  },
  "& .MuiFormHelperText-root": {
    color: "#d53737",
  },
  "&.MuiTextField-root": {
    borderRadius: 8,
    borderColor: "#757575",
    "& .MuiOutlinedInput-root": {
      borderColor: "#757575",
      borderRadius: 8,
      height: 44,
      "&.Mui-disabled": {
        backgroundColor: disabledBgColor || "#f5f5f5", // Lighter background for disabled inputs
        border: "1px solid #d6d6d6", // Uniform edge to avoid double lines
      },
      "&.MuiInputBase-multiline": {
        height: "auto"
      }
    }
  }
}));

const StringNumber = ({
  name,
  id,
  value = "",
  onChange,
  defaultPlaceholder,
  errorText,
  maxLength,
  disabled,
  className,
  withCounter,
  error: externalError,
  multiline = false,
  rows = 1,
  disabledBgColor,
}) => {
  const [error, setError] = useState(!!errorText || !!externalError);
  const [helperText, setHelperText] = useState(errorText);
  const [remainingChars, setRemainingChars] = useState(maxLength || 0);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Update error state when errorText prop changes
  useEffect(() => {
    setHelperText(errorText);
    setError(!!errorText || !!externalError);
  }, [errorText, externalError]);

  // Update remaining characters when value changes
  useEffect(() => {
    if (maxLength) {
      setRemainingChars(maxLength - (value?.length || 0));
    }
  }, [value, maxLength]);

  const handleChange = (event) => {
    let newValue = event.target.value;
    setHasInteracted(true);

    // Check for max length
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
      setHelperText(`Cannot exceed ${maxLength} characters`);
      setError(true);
    } else {
      // Only clear internal error message, not external error
      if (!errorText) {
        setHelperText("");
        setError(false);
      }
    }

    // Pass the modified event to parent
    onChange({
      ...event,
      target: {
        ...event.target,
        value: newValue,
      },
    });
  };

  // This ensures the field is validated when it loses focus
  const handleBlur = () => {
    if (hasInteracted && value.trim() === "") {
      // Only set error if the field is required (indicated by errorText being set for empty value)
      if (errorText) {
        setError(true);
        setHelperText(errorText);
      }
    }
  };

  return (
    <Fragment>
      <CustomizedTextField
        fullWidth
        variant="outlined"
        size="small"
        error={error}
        placeholder={defaultPlaceholder || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value}
        name={name}
        disabled={disabled}
        className={clsx(className)}
        id={id}
        multiline={multiline}
        rows={rows}
        disabledBgColor={disabledBgColor}
      />
      <div
        className={`inputHelperTextContainer ${error ? "spaceBetween" : "flexEnd"
          }`}
      >
        {error && <div className={clsx("helperText", { error })}>{helperText}</div>}
        {withCounter && maxLength && (
          <div className={clsx("counter", { limit: remainingChars === 0 })}>
            {remainingChars}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default StringNumber;
